// rum.ts
import { AwsRum, AwsRumConfig } from 'aws-rum-web';

type RumAction<T> = (client: AwsRum) => T;
type RumRecordEventPayload = Parameters<AwsRum['recordEvent']>[1];
type RumRecordPageViewPayload = Parameters<AwsRum['recordPageView']>[0];
type RumSessionAttributes = Parameters<AwsRum['addSessionAttributes']>[0];
type RumDomEvents = Parameters<AwsRum['registerDomEvents']>[0];
type InitRumOptions = {
  force?: boolean;
};

let awsRum: AwsRum | undefined;
let hasAttemptedInit = false;

const APPLICATION_ID = 'd663d2d4-66e3-47d1-bee9-1a624f0fb443';
const APPLICATION_REGION = 'us-west-2';
const APPLICATION_VERSION = '1.0.0';

const RUM_CONFIG: AwsRumConfig = {
  endpoint: `https://dataplane.rum.${APPLICATION_REGION}.amazonaws.com`,
  identityPoolId: 'us-west-2:b7a1e3bb-04ac-413e-a84c-7465e915f937',
  guestRoleArn:
    'arn:aws:iam::674674125182:role/Koru-RumStack-rogerdev-RumUnauthRoleKoru890D9D6B-FxpE6R6WQLaz',
  sessionSampleRate: 1,
  telemetries: ['performance', 'errors', 'http'],
  allowCookies: true,
  enableXRay: true,
  signing: true,
  sessionEventLimit: 10000
};

function runWithRum<T>(label: string, action: RumAction<T>): T | undefined {
  const client = initRum();

  if (!client) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[RUM] ${label} skipped because the client is not ready.`);
    }
    return undefined;
  }

  try {
    return action(client);
  } catch (error) {
    console.warn(`[RUM] ${label} failed:`, error);
    return undefined;
  }
}

export function initRum(options: InitRumOptions = {}): AwsRum | undefined {
  const { force = false } = options;

  if (awsRum && !force) return awsRum;
  if (hasAttemptedInit && !force) return awsRum;

  hasAttemptedInit = !force;

  if (force) {
    awsRum = undefined;
    hasAttemptedInit = false;
  }

  try {
    awsRum = new AwsRum(
      APPLICATION_ID,
      APPLICATION_VERSION,
      APPLICATION_REGION,
      RUM_CONFIG
    );
    hasAttemptedInit = true;
  } catch (error) {
    console.warn('RUM init failed:', error);
    hasAttemptedInit = true;
  }

  return awsRum;
}

export function getRumClient(): AwsRum | undefined {
  return awsRum;
}

export function withRum<T>(
  action: RumAction<T>,
  label = 'custom action'
): T | undefined {
  return runWithRum(label, action);
}

export function recordRumEvent(
  eventType: string,
  eventData: RumRecordEventPayload = {}
): void {
  runWithRum('recordEvent', (client) =>
    client.recordEvent(eventType, eventData)
  );
}

export function recordRumError(error: unknown): void {
  runWithRum('recordError', (client) => client.recordError(error));
}

export function recordRumPageView(payload: RumRecordPageViewPayload): void {
  runWithRum('recordPageView', (client) => client.recordPageView(payload));
}

export function addRumSessionAttributes(
  attributes: RumSessionAttributes
): void {
  runWithRum('addSessionAttributes', (client) =>
    client.addSessionAttributes(attributes)
  );
}

export function registerRumDomEvents(events: RumDomEvents): void {
  runWithRum('registerDomEvents', (client) => client.registerDomEvents(events));
}

export function dispatchRumEvents(): void {
  runWithRum('dispatch', (client) => client.dispatch());
}

export function dispatchRumEventsWithBeacon(): void {
  runWithRum('dispatchBeacon', (client) => client.dispatchBeacon());
}

export function enableRum(): void {
  runWithRum('enable', (client) => client.enable());
}

export function disableRum(): void {
  runWithRum('disable', (client) => client.disable());
}

export function allowRumCookies(allow: boolean): void {
  runWithRum('allowCookies', (client) => client.allowCookies(allow));
}

export type RumClient = AwsRum;
